import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, RefreshCw, Search } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { OpportunityBoard } from "@/components/opportunities/OpportunityBoard";
import { OpportunityDialog } from "@/components/opportunities/OpportunityDialog";
import { CreateOpportunityDialog } from "@/components/opportunities/CreateOpportunityDialog";
import { DeadlineBadge } from "@/components/opportunities/DeadlineBadge";
import {
  createOpportunity,
  deleteOpportunity,
  fetchOpportunities,
  formatCop,
  isOverdue,
  isUrgent,
  runSecopSweep,
  updateOpportunity,
} from "@/components/opportunities/opportunitiesService";
import {
  ACTIONABLE_STATUSES,
  URGENT_DAYS,
} from "@/components/opportunities/opportunityConstants";
import type {
  Opportunity,
  OpportunityInsert,
  OpportunityStatus,
  OpportunityUpdate,
} from "@/components/opportunities/opportunityTypes";

export default function Opportunities() {
  const { companyId, role, loading: userLoading } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Opportunity | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const canDelete = role === "owner" || role === "admin";
  const queryKey = ["opportunities", companyId];

  const { data: opportunities, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchOpportunities(companyId as number),
    enabled: !!companyId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const failed = (action: string) => (error: unknown) => {
    toast({
      variant: "destructive",
      title: `Could not ${action}`,
      description: error instanceof Error ? error.message : "Unexpected error",
    });
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OpportunityStatus }) =>
      updateOpportunity(id, { status }),
    onSuccess: invalidate,
    onError: failed("move that opportunity"),
  });

  const saveMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: OpportunityUpdate }) =>
      updateOpportunity(id, patch),
    onSuccess: () => {
      invalidate();
      toast({ title: "Saved" });
    },
    onError: failed("save"),
  });

  const createMutation = useMutation({
    mutationFn: (payload: OpportunityInsert) => createOpportunity(payload),
    onSuccess: () => {
      invalidate();
      toast({ title: "Opportunity added" });
    },
    onError: failed("add that opportunity"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOpportunity(id),
    onSuccess: () => {
      invalidate();
      setDetailOpen(false);
      toast({ title: "Deleted" });
    },
    onError: failed("delete"),
  });

  const sweepMutation = useMutation({
    mutationFn: () => runSecopSweep(companyId as number, 7),
    onSuccess: (count) => {
      invalidate();
      toast({
        title: "SECOP sweep complete",
        description:
          count === 0
            ? "Nothing new matched in the last 7 days."
            : `${count} process${count === 1 ? "" : "es"} checked and synced.`,
      });
    },
    onError: failed("run the sweep"),
  });

  const filtered = useMemo(() => {
    const all = opportunities ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) =>
      [o.title, o.entity, o.department, o.description]
        .filter(Boolean)
        .some((field) => (field as string).toLowerCase().includes(q)),
    );
  }, [opportunities, search]);

  const stats = useMemo(() => {
    const all = opportunities ?? [];
    const open = all.filter((o) => ACTIONABLE_STATUSES.includes(o.status));
    return {
      open: open.length,
      urgent: all.filter((o) => isUrgent(o, URGENT_DAYS)).length,
      overdue: all.filter(isOverdue).length,
      expired: all.filter((o) => o.status === "Expired").length,
      pipelineValue: open.reduce((sum, o) => sum + (o.budget_cop ?? 0), 0),
    };
  }, [opportunities]);

  /** Open, actionable, closing soon or already past — the only list that must never be missed. */
  const attention = useMemo(
    () =>
      (opportunities ?? [])
        .filter((o) => isUrgent(o, URGENT_DAYS) || isOverdue(o))
        .sort((a, b) => (a.deadline ?? "").localeCompare(b.deadline ?? "")),
    [opportunities],
  );

  const openDetail = (o: Opportunity) => {
    setSelected(o);
    setDetailOpen(true);
  };

  if (userLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          No company is associated with your profile, so there is no funnel to show.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Opportunities</h1>
          <p className="text-sm text-muted-foreground">
            Public-sector funnel — SECOP tenders, convocatorias, innovation calls and
            direct-award leads. Swept automatically twice a week.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => sweepMutation.mutate()}
            disabled={sweepMutation.isPending}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${sweepMutation.isPending ? "animate-spin" : ""}`}
            />
            {sweepMutation.isPending ? "Sweeping…" : "Sweep SECOP"}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Open" value={String(stats.open)} />
        <StatCard
          label={`Closing ≤ ${URGENT_DAYS}d`}
          value={String(stats.urgent)}
          tone={stats.urgent > 0 ? "warn" : undefined}
        />
        <StatCard
          label="Past deadline"
          value={String(stats.overdue)}
          tone={stats.overdue > 0 ? "danger" : undefined}
        />
        <StatCard
          label="Expired unfiled"
          value={String(stats.expired)}
          tone={stats.expired > 0 ? "danger" : undefined}
        />
        <StatCard label="Open pipeline" value={formatCop(stats.pipelineValue)} />
      </section>

      {attention.length > 0 && (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            Needs attention now
          </h2>
          <ul className="space-y-1.5">
            {attention.map((o) => (
              <li key={o.id}>
                <button
                  onClick={() => openDetail(o)}
                  className="flex w-full flex-wrap items-center gap-2 rounded px-1 py-0.5 text-left text-sm hover:bg-amber-100 dark:hover:bg-amber-900/40"
                >
                  <DeadlineBadge deadline={o.deadline} />
                  <span className="font-medium">{o.title}</span>
                  {o.entity && (
                    <span className="text-xs text-muted-foreground">· {o.entity}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title, entity, department…"
          className="pl-8"
        />
      </div>

      {isLoading ? (
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-[260px] shrink-0" />
          ))}
        </div>
      ) : (
        <OpportunityBoard
          opportunities={filtered}
          onOpen={openDetail}
          onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
        />
      )}

      <OpportunityDialog
        opportunity={selected}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onSave={async (id, patch) => {
          await saveMutation.mutateAsync({ id, patch });
        }}
        onDelete={async (id) => {
          await deleteMutation.mutateAsync(id);
        }}
        canDelete={canDelete}
      />

      <CreateOpportunityDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        companyId={companyId}
        onCreate={async (payload) => {
          await createMutation.mutateAsync(payload);
        }}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn" | "danger";
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === "danger"
            ? "text-red-600 dark:text-red-400"
            : tone === "warn"
              ? "text-amber-600 dark:text-amber-400"
              : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
